import React, { useState, useMemo } from 'react';
import styled, { keyframes, css } from 'styled-components';
import useWellness from 'state/wellness';
import useUser from 'state/user';

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const pulse = keyframes`
    0%   { box-shadow: 0 0 0 0 rgba(255, 140, 0, 0.5); }
    70%  { box-shadow: 0 0 0 10px rgba(255, 140, 0, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 140, 0, 0); }
`;

const slideUp = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

// ---------------------------------------------------------------------------
// SOS Button — siempre visible (sticky)
// ---------------------------------------------------------------------------

const SOSBtnWrap = styled.button`
    position: fixed;
    bottom: 90px;
    right: 16px;
    z-index: 200;
    background: #FF8C00;
    border: none;
    border-radius: 50%;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 20px;
    box-shadow: 0 3px 12px rgba(255, 140, 0, 0.4);
    animation: ${pulse} 3s ease-in-out infinite;
    -webkit-tap-highlight-color: transparent;
    transition: transform 0.15s ease;

    &:active {
        transform: scale(0.9);
    }
`;

// ---------------------------------------------------------------------------
// Overlay + Bottom Sheet
// ---------------------------------------------------------------------------

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 300;
    animation: ${fadeIn} 0.2s ease;
`;

const Sheet = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 400;
    background: #fff;
    border-radius: 24px 24px 0 0;
    padding: 20px 20px 40px;
    animation: ${slideUp} 0.3s ease;
    max-width: 480px;
    margin: 0 auto;
`;

const SheetHandle = styled.div`
    width: 40px;
    height: 4px;
    background: #e0e0e0;
    border-radius: 2px;
    margin: 0 auto 20px;
`;

const SheetTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
`;

const SheetTitleText = styled.h2`
    font-size: 20px;
    font-weight: 800;
    color: #1a1a1a;
    margin: 0;
`;

const SheetSubtitle = styled.p`
    font-size: 13px;
    color: #777;
    margin: 0 0 20px;
    line-height: 1.5;
`;

const SOSOptionBtn = styled.a`
    display: flex;
    align-items: center;
    gap: 14px;
    background: ${({ bg }) => bg || '#f5f5f5'};
    border-radius: 16px;
    padding: 16px;
    margin-bottom: 12px;
    text-decoration: none;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: opacity 0.15s;

    &:active {
        opacity: 0.75;
    }
`;

const SOSOptionIcon = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: ${({ color }) => color};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    flex-shrink: 0;
`;

const SOSOptionText = styled.div`
    flex: 1;
`;

const SOSOptionTitle = styled.p`
    font-size: 15px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 2px;
`;

const SOSOptionDesc = styled.p`
    font-size: 12px;
    color: #777;
    margin: 0;
`;

const CloseBtn = styled.button`
    width: 100%;
    background: none;
    border: 1.5px solid #e0e0e0;
    border-radius: 14px;
    padding: 14px;
    font-size: 14px;
    font-weight: 600;
    color: #999;
    cursor: pointer;
    margin-top: 4px;
    -webkit-tap-highlight-color: transparent;
`;

// ---------------------------------------------------------------------------
// SOS Options data
// ---------------------------------------------------------------------------

const BASE_OPTIONS = [
    {
        id: 'lifeline',
        actionType: 'saptel',
        icon: '📞',
        iconBg: '#FFF3CD',
        title: 'Línea Nacional de Prevención',
        desc: 'SAPTEL: 55 5259-8121 · 24 horas, gratuita',
        href: 'tel:5552598121',
        bg: '#FFFBF0',
    },
    {
        id: 'emergency',
        actionType: '911',
        icon: '🚨',
        iconBg: '#FDECEA',
        title: 'Llamar a Emergencias',
        desc: '911 — Servicios de emergencia nacionales',
        href: 'tel:911',
        bg: '#FFF5F5',
    },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SOSButton = ({ onNavigate }) => {
    const [open, setOpen] = useState(false);
    const [, wellnessDispatcher] = useWellness();
    const [user] = useUser();

    // Build 3rd option dynamically based on emergency contact
    const emergencyContact = user.current?.extraData?.client;
    const contactName = emergencyContact?.emergencyContactName;
    const contactPhone = emergencyContact?.emergencyContactPhone;

    const options = useMemo(() => {
        const thirdOption = contactName && contactPhone
            ? {
                id: 'contact',
                actionType: 'contact',
                icon: '💚',
                iconBg: '#E8F5E9',
                title: `Llamar a ${contactName}`,
                desc: `Tu contacto de confianza: ${contactPhone}`,
                href: `tel:${contactPhone.replace(/\s/g, '')}`,
                bg: '#F1F8E9',
            }
            : {
                id: 'contact',
                actionType: 'contact',
                icon: '💚',
                iconBg: '#E8F5E9',
                title: 'Añadir contacto de confianza',
                desc: 'Configura un contacto de emergencia en tu perfil',
                href: '/perfil',
                bg: '#F1F8E9',
                isInternal: true,
            };
        return [...BASE_OPTIONS, thirdOption];
    }, [contactName, contactPhone]);

    const handleOption = (opt) => {
        setOpen(false);
        // Log silencioso — clínica silenciosa (el paciente no sabe)
        wellnessDispatcher.logSosStart({ actionType: opt.actionType });
        if (opt.isInternal && onNavigate) {
            onNavigate(opt.href);
        }
    };

    return (
        <>
            <SOSBtnWrap
                aria-label="Botón de emergencia"
                onClick={() => setOpen(true)}
                title="Ayuda de emergencia"
            >
                🛟
            </SOSBtnWrap>

            {open && (
                <>
                    <Overlay onClick={() => setOpen(false)} />
                    <Sheet>
                        <SheetHandle />
                        <SheetTitle>
                            <span style={{ fontSize: 28 }}>🛟</span>
                            <SheetTitleText>¿Necesitas ayuda ahora?</SheetTitleText>
                        </SheetTitle>
                        <SheetSubtitle>
                            Estás en un espacio seguro. Elige cómo quieres recibir apoyo.
                        </SheetSubtitle>

                        {options.map((opt) => (
                            <SOSOptionBtn
                                key={opt.id}
                                href={opt.isInternal ? undefined : opt.href}
                                bg={opt.bg}
                                onClick={() => handleOption(opt)}
                            >
                                <SOSOptionIcon color={opt.iconBg}>
                                    {opt.icon}
                                </SOSOptionIcon>
                                <SOSOptionText>
                                    <SOSOptionTitle>{opt.title}</SOSOptionTitle>
                                    <SOSOptionDesc>{opt.desc}</SOSOptionDesc>
                                </SOSOptionText>
                                <span style={{ fontSize: 20, color: '#ccc' }}>›</span>
                            </SOSOptionBtn>
                        ))}

                        <CloseBtn onClick={() => setOpen(false)}>
                            Cerrar
                        </CloseBtn>
                    </Sheet>
                </>
            )}
        </>
    );
};

export default SOSButton;
